import axios from 'axios'

import {
  extractLevelProfile,
  type LevelProfileAnalyticsType,
  type LevelProfileFor,
  type LevelProfileSnapshot,
} from '@/service/analytics/level-profiles.types'
import { orderXClient } from '@/service/client'
import type { DataPayload, GetAnalyticSnapshotRequest } from '@/types/generated/public_api'

// A 404 here means the venue publisher hasn't reached this symbol yet — an expected,
// terminal state, not an error. Every other status (403 entitlement gate, 500, etc.) still
// rejects normally. Checked against the real Axios response status, not `(e as ErrorResponse)
// .status` — the shared response interceptor (service/client.ts) rewrites `error.response.data`
// to the decoded error body but leaves the real HTTP status on `error.response.status`; the
// `ErrorResponse{status,message}` shape some sibling modules cast errors to is only accurate for
// the *synthetic* rejections those modules construct themselves, not for real network errors.
export async function fetchLevelProfileSnapshot<T extends LevelProfileAnalyticsType>(
  params: GetAnalyticSnapshotRequest & { analyticsType: T },
  signal?: AbortSignal
): Promise<LevelProfileSnapshot<LevelProfileFor<T>>> {
  try {
    const { data } = await orderXClient.post<DataPayload>(
      '/api',
      { getAnalyticSnapshot: params },
      { headers: { 'X-Decode-Key': 'datapayload' }, signal }
    )

    const profile = extractLevelProfile(data, params.analyticsType)
    if (!profile) {
      // A 200 with no matching DataPayload arm is a malformed response, not "not published" —
      // NotFound is a distinct backend signal (see the catch block below), not inferred from an
      // empty success body.
      throw new Error('Level profile snapshot response did not include the requested payload.')
    }

    return { status: 'published', profile }
  } catch (e) {
    if (axios.isAxiosError(e) && e.response?.status === 404) {
      return { status: 'not-published' }
    }
    throw e
  }
}
